import { interpolateRgbBasis } from 'd3-interpolate';
import { scaleDiverging, scaleSequential } from 'd3-scale';
import { interpolateRdBu } from 'd3-scale-chromatic';
import { decimal, inteiro, pct } from '../format';
import type { Camada, MapaEntrada } from '../types';

/** Município sem dado: o desenho usa um padrão hachurado, não uma cor da escala (não confundir "zero" com "sem dado"). */
export const SEM_DADO = 'url(#semdado)';

export type Escala = {
  /** Cor de preenchimento para um valor (ou o padrão hachurado quando não há dado). */
  cor: (valor: number | null | undefined) => string;
  dominio: [number, number];
  /** Pontos da escala para montar o gradiente da legenda. */
  gradiente: string[];
  rotulo: (v: number) => string;
  titulo: string;
};

const TITULOS: Record<Camada, string> = {
  progresso: 'Progresso médio das ações',
  cobertura: 'Secretarias atuando no município',
  atrasadas: 'Ações atrasadas',
  satisfacao: 'Aprovação do governo',
  indicadores: 'Indicador oficial',
};
export const tituloCamada = (c: Camada) => TITULOS[c];

/** Tema da tela. No escuro, "mais claro/brilhante" = mais; no claro, "mais escuro/saturado" = mais (o fundo é claro). */
export type TemaEscala = 'claro' | 'escuro';

const PALETAS: Record<TemaEscala, Record<'progresso' | 'cobertura' | 'atrasadas', readonly string[]>> = {
  escuro: {
    progresso: ['#1e293b', '#1e4a7a', '#0e8f9a', '#3fd0a0', '#c4f26b'],
    cobertura: ['#1e293b', '#3b4f8f', '#6d7be0', '#b7c0ff'],
    atrasadas: ['#1e293b', '#6b1d2b', '#dc3545', '#ffb3b8'],
  },
  claro: {
    progresso: ['#dbe4ee', '#9ccfe6', '#3fb8a8', '#1f9d55', '#0b5d2a'],
    cobertura: ['#dbe4ee', '#b4bff0', '#6d7be0', '#2f3a9e'],
    atrasadas: ['#dbe4ee', '#f7b4bb', '#e0475a', '#7a1224'],
  },
};

const PALETAS_INDICADOR: Record<TemaEscala, { neutro: readonly string[]; direcional: readonly string[] }> = {
  // direcional: do pior (início) ao melhor (fim)
  escuro: { neutro: ['#1e293b', '#1e3a5f', '#2563eb', '#60a5fa', '#bfdbfe'], direcional: ['#3b1d2a', '#8b2f3f', '#d97706', '#65a30d', '#bef264'] },
  claro: { neutro: ['#dbe4ee', '#a9c4ee', '#4f86e8', '#1d4ed8', '#12307f'], direcional: ['#a11d3a', '#e8793a', '#e3b53b', '#7fb82f', '#14803c'] },
};

const arredondaAcima = (n: number, passo: number) => Math.ceil(n / passo) * passo;

export function dominioDaCamada(camada: Camada, max: number | null): [number, number] {
  switch (camada) {
    case 'progresso':
    case 'satisfacao':
      return [0, 100];
    case 'cobertura':
      return [0, Math.max(2, max ?? 0)];
    case 'atrasadas':
      return [0, Math.min(100, Math.max(10, arredondaAcima(max ?? 0, 10)))];
    case 'indicadores':
      return [0, 100]; // não usado: a escala dos indicadores é por quantis (criarEscalaIndicador)
  }
}

export function criarEscala(camada: Exclude<Camada, 'indicadores'>, escalaApi: { min: number | null; max: number | null }, tema: TemaEscala = 'escuro'): Escala {
  const dominio = dominioDaCamada(camada, escalaApi.max);
  const [lo, hi] = dominio;

  let interpola: (t: number) => string;
  if (camada === 'satisfacao') {
    // Divergente em torno de 50%: vermelho = reprova, azul = aprova.
    const d = scaleDiverging(interpolateRdBu).domain([0, 50, 100]).clamp(true);
    interpola = (v) => d(v);
  } else {
    const s = scaleSequential(interpolateRgbBasis([...PALETAS[tema][camada]])).domain(dominio).clamp(true);
    interpola = (v) => s(v);
  }

  const passos = 9;
  const gradiente = Array.from({ length: passos }, (_, i) => interpola(lo + ((hi - lo) * i) / (passos - 1)));
  const unid = camada === 'cobertura' ? '' : '%';

  return {
    cor: (v) => (v === null || v === undefined ? SEM_DADO : interpola(v)),
    dominio,
    gradiente,
    rotulo: (v) => (unid ? `${inteiro(v)}%` : inteiro(v)),
    titulo: TITULOS[camada],
  };
}

/**
 * Escala de um indicador oficial. Usa QUANTIS (cada cor cobre a mesma quantidade de municípios), porque indicadores como
 * população, PIB e gasto são muito assimétricos: numa escala linear, um município gigante deixaria os outros 222 da mesma cor.
 * Quanto mais claro/brilhante, MELHOR (para indicador "menor é melhor" a escala é invertida); indicador neutro usa
 * azul, para ninguém ler "bom" ou "ruim" onde não existe.
 */
export function criarEscalaIndicador(valores: number[], sentido: 'MAIOR_MELHOR' | 'MENOR_MELHOR' | 'NEUTRO', rotulo: (v: number) => string, titulo: string, tema: TemaEscala = 'escuro'): Escala & { rotulosLegenda: [string, string, string] } {
  const ordenados = [...valores].sort((a, b) => a - b);
  const paletas = PALETAS_INDICADOR[tema];
  const paleta = sentido === 'NEUTRO' ? paletas.neutro : paletas.direcional;
  const base = interpolateRgbBasis([...paleta]);
  const inverte = sentido === 'MENOR_MELHOR';
  const cor = (t: number) => base(inverte ? 1 - t : t);

  // posição do valor entre os quantis (0..1): fração de municípios com valor menor, empates no meio
  const posicao = (v: number) => {
    if (ordenados.length < 2) return 0.5;
    let lo = 0;
    let hi = ordenados.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (ordenados[m]! < v) lo = m + 1; else hi = m; }
    let hi2 = lo;
    while (hi2 < ordenados.length && ordenados[hi2] === v) hi2++;
    return ((lo + hi2) / 2) / ordenados.length;
  };

  const min = ordenados[0] ?? 0;
  const max = ordenados[ordenados.length - 1] ?? 0;
  const mediana = ordenados.length ? ordenados[Math.floor(ordenados.length / 2)]! : 0;
  const passos = 9;
  return {
    cor: (v) => (v === null || v === undefined ? SEM_DADO : cor(posicao(v))),
    dominio: [min, max],
    gradiente: Array.from({ length: passos }, (_, i) => cor(i / (passos - 1))),
    rotulo,
    titulo,
    // Rótulos nos quantis 0, 50 e 100%: o que a barra mostra de fato (mínimo, mediana, máximo).
    rotulosLegenda: [rotulo(min), rotulo(mediana), rotulo(max)],
  };
}

/** Linhas de texto do tooltip de um município, por camada. */
export function textoTooltip(camada: Camada, e: MapaEntrada | undefined, amostraMinima?: number): string[] {
  if (camada === 'satisfacao') {
    if (!e || e.total === 0) return ['Sem respostas de pesquisa'];
    if (e.disponivel === false || e.valor === null) {
      return [`Amostra insuficiente`, `${inteiro(e.total)} ${e.total === 1 ? 'resposta' : 'respostas'}${amostraMinima ? ` (mínimo ${inteiro(amostraMinima)})` : ''}`];
    }
    return [
      `Aprovação ${pct(e.valor)}${e.margemErro != null ? ` ±${decimal(e.margemErro)}` : ''}`,
      `${inteiro(e.total)} respostas${e.meta ? ` de ${inteiro(e.meta)} previstas` : ''}`,
    ];
  }
  if (!e || e.total === 0 || e.valor === null) return ['Sem ações cadastradas'];

  const acoes = `${inteiro(e.total)} ${e.total === 1 ? 'ação' : 'ações'}`;
  const atrasadas = e.atrasadas > 0 ? ` · ${inteiro(e.atrasadas)} ${e.atrasadas === 1 ? 'atrasada' : 'atrasadas'}` : '';
  switch (camada) {
    case 'progresso':
      return [`Progresso ${pct(e.valor)}`, acoes + atrasadas];
    case 'cobertura':
      return [`${inteiro(e.valor)} ${e.valor === 1 ? 'secretaria' : 'secretarias'}`, acoes];
    case 'atrasadas':
      return [`${pct(e.valor)} das ações atrasadas`, `${inteiro(e.atrasadas)} de ${inteiro(e.total)}`];
    default:
      return [];
  }
}
